import { ImageAnalysisSlideJobStatus } from 'api/jobs';
import { JobStatus, JobType } from 'interfaces/job';
import { filter, keyBy, map, size } from 'lodash';

import { StudyWorkflowImageAnalysisSlideScopes } from './studyWorkflowInventory';

export interface ImageAnalysisTaskStatus {
  id: string;
  modalityLabel: string;
  label: string;
  totalCount: number;
  completedCount: number;
  runningCount: number;
  pendingCount: number;
  failedCount: number;
  cancelledCount: number;
  notStartedCount: number;
}

export const getImageAnalysisTaskStatuses = (
  slideScopes: StudyWorkflowImageAnalysisSlideScopes,
  statuses: ImageAnalysisSlideJobStatus[]
): ImageAnalysisTaskStatus[] => {
  const statusBySlideAndJobType = keyBy(statuses, ({ slideId, jobType }) => `${slideId}:${jobType}`);
  const taskScopes = [
    ...slideScopes.tissueSegmentationScopes,
    {
      id: 'dapi-normalization',
      modalityLabel: 'Multiplex',
      label: 'DAPI normalization',
      slideIds: slideScopes.multiplexSlideIds,
      jobType: JobType.MultiplexNormalization,
    },
    {
      id: 'cell-segmentation',
      modalityLabel: 'Multiplex',
      label: 'Cell segmentation',
      slideIds: slideScopes.multiplexSlideIds,
      jobType: JobType.MultiplexCellSegmentation,
    },
  ];

  return map(taskScopes, (task) =>
    countImageAnalysisTaskStatus({
      id: task.id,
      modalityLabel: task.modalityLabel,
      label: task.label,
      slideIds: task.slideIds,
      jobType: task.jobType,
      statusBySlideAndJobType,
    })
  );
};

export const getRnaSpotDetectionTaskStatus = (
  multiplexSlideIds: string[],
  statuses: ImageAnalysisSlideJobStatus[]
): ImageAnalysisTaskStatus =>
  countImageAnalysisTaskStatus({
    id: 'rna-spot-detection',
    modalityLabel: 'Multiplex',
    label: 'RNA probes spot detection',
    slideIds: multiplexSlideIds,
    jobType: JobType.MultiplexPeakDetection,
    statusBySlideAndJobType: keyBy(statuses, ({ slideId, jobType }) => `${slideId}:${jobType}`),
  });

const countImageAnalysisTaskStatus = ({
  id,
  modalityLabel,
  label,
  slideIds,
  jobType,
  statusBySlideAndJobType,
}: {
  id: string;
  modalityLabel: string;
  label: string;
  slideIds: string[];
  jobType: JobType;
  statusBySlideAndJobType: Record<string, ImageAnalysisSlideJobStatus>;
}): ImageAnalysisTaskStatus => {
  const slideStatuses = map(slideIds, (slideId) => statusBySlideAndJobType[`${slideId}:${jobType}`]?.status);

  return {
    id,
    modalityLabel,
    label,
    totalCount: size(slideIds),
    completedCount: size(filter(slideStatuses, (status) => status === JobStatus.Completed)),
    runningCount: size(filter(slideStatuses, (status) => status === JobStatus.Running)),
    pendingCount: size(filter(slideStatuses, (status) => status === JobStatus.Pending)),
    failedCount: size(
      filter(slideStatuses, (status) => status === JobStatus.Failed || status === JobStatus.FailedToStart)
    ),
    cancelledCount: size(filter(slideStatuses, (status) => status === JobStatus.Cancelled)),
    notStartedCount: size(filter(slideStatuses, (status) => !status)),
  };
};
